import React, { useState } from "react";
import { Plus, Minus, Calendar } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Label } from "@/shadcn-components/ui/label";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn-components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn-components/ui/popover";
import { Calendar as CalendarComponent } from "@/shadcn-components/ui/calendar";
import { format } from "date-fns";

interface TradeStopLimitContentProps {
  orderType: string;
  type?: "new" | "modify" | "closeBy";
  bidPrice: number;
  askPrice: number;
  formatPrice: (price: number) => string;
  price: number | null;
  priceInput: string;
  stopLimitPrice: number | null;
  stopLimitPriceInput: string;
  stopLoss: number | null;
  stopLossInput: string;
  takeProfit: number | null;
  takeProfitInput: string;
  onPriceChange: (change: number) => void;
  onPriceInputChange: (value: string) => void;
  onStopLimitPriceChange: (change: number) => void;
  onStopLimitPriceInputChange: (value: string) => void;
  onStopLossChange: (change: number) => void;
  onStopLossInputChange: (value: string) => void;
  onTakeProfitChange: (change: number) => void;
  onTakeProfitInputChange: (value: string) => void;
  onPlaceOrder: () => void;
  isTrading: boolean;
}

export default function TradeStopLimitContent({
  orderType,
  type,
  bidPrice,
  askPrice,
  formatPrice,
  price,
  priceInput,
  stopLimitPrice,
  stopLimitPriceInput,
  stopLoss,
  stopLossInput,
  takeProfit,
  takeProfitInput,
  onPriceChange,
  onPriceInputChange,
  onStopLimitPriceChange,
  onStopLimitPriceInputChange,
  onStopLossChange,
  onStopLossInputChange,
  onTakeProfitChange,
  onTakeProfitInputChange,
  onPlaceOrder,
  isTrading,
}: TradeStopLimitContentProps) {
  
  // Expiration state
  const [expirationType, setExpirationType] = useState<string>("GTC");
  const [expirationDate, setExpirationDate] = useState<Date | undefined>(undefined);
  const [expirationTime, setExpirationTime] = useState<string>("12:00");
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Check if order type supports expiration
  const supportsExpiration = orderType.includes("Stop Limit");

  // Format expiration display
  const getExpirationDisplay = () => {
    if (expirationType === "GTC") return "GTC";
    if (expirationType === "TODAY") return "Today";
    if (expirationType === "SPECIFIED") {
      if (expirationDate) {
        return `${format(expirationDate, "MMM dd, yyyy")} ${expirationTime}`;
      }
      return "Select date";
    }
    if (expirationType === "SPECIFIED_DAY") {
      if (expirationDate) {
        return format(expirationDate, "MMM dd, yyyy");
      }
      return "Select date";
    }
    return "GTC";
  };

  // Handle expiration type change
  const handleExpirationTypeChange = (value: string) => {
    setExpirationType(value);
    const today = new Date();
    
    if (value === "TODAY") {
      setExpirationDate(today);
      setExpirationTime("23:59");
    } else if (value === "SPECIFIED") {
      setExpirationDate(today);
      setExpirationTime("12:00");
    } else if (value === "SPECIFIED_DAY") {
      setExpirationDate(today);
    }
  };
  
  // Helper to set default price based on order type
  const handlePriceClick = () => {
    if (!price) {
      let defaultPrice;
      if (orderType.includes("Buy")) {
        defaultPrice = askPrice * 1.001; // Slightly above ask for buy stop limit
      } else if (orderType.includes("Sell")) {
        defaultPrice = bidPrice * 0.999; // Slightly below bid for sell stop limit
      } else {
        defaultPrice = (bidPrice + askPrice) / 2;
      }
      
      if (defaultPrice > 0) {
        onPriceInputChange(defaultPrice.toString());
      }
    }
  };

  // Helper to set default stop limit price
  const handleStopLimitPriceClick = () => {
    if (!stopLimitPrice) {
      let defaultStopLimitPrice;
      if (orderType.includes("Buy")) {
        defaultStopLimitPrice = askPrice; // Use current ask for buy stop limit
      } else if (orderType.includes("Sell")) {
        defaultStopLimitPrice = bidPrice; // Use current bid for sell stop limit
      } else {
        defaultStopLimitPrice = (bidPrice + askPrice) / 2;
      }
      
      if (defaultStopLimitPrice > 0) {
        onStopLimitPriceInputChange(defaultStopLimitPrice.toString());
      }
    }
  };

  // Helper to set default stop loss
  const handleStopLossClick = () => {
    if (!stopLoss) {
      let defaultStopLoss;
      if (orderType.includes("Buy")) {
        defaultStopLoss = bidPrice * 0.99; // 1% below current bid for buy orders
      } else {
        defaultStopLoss = askPrice * 1.01; // 1% above current ask for sell orders
      }
      
      if (defaultStopLoss > 0) {
        onStopLossInputChange(defaultStopLoss.toString());
      }
    }
  };

  // Helper to set default take profit
  const handleTakeProfitClick = () => {
    if (!takeProfit) {
      let defaultTakeProfit;
      if (orderType.includes("Buy")) {
        defaultTakeProfit = askPrice * 1.01; // 1% above current ask for buy orders
      } else {
        defaultTakeProfit = bidPrice * 0.99; // 1% below current bid for sell orders
      }
      
      if (defaultTakeProfit > 0) {
        onTakeProfitInputChange(defaultTakeProfit.toString());
      }
    }
  };

  // Determine button text based on type and orderType
  const getButtonText = () => {
    if (type === "modify") return "MODIFY";
    if (type === "closeBy") return "CLOSE";
    if (orderType === "Modify") return "MODIFY";
    if (orderType === "Close By") return "CLOSE";
    return "PLACE ORDER";
  };

  // Determine button and card colors based on orderType
  const getButtonColor = () => {
    if (orderType.includes("Buy")) return "bg-blue-600 hover:bg-blue-700";
    if (orderType.includes("Sell")) return "bg-red-600 hover:bg-red-700";
    return "bg-gray-600 hover:bg-gray-700";
  };

  const getCardBorderColor = () => {
    return "";
  };

  const getInputFocusColor = () => {
    return "";
  };

  return (
    <>
      {/* Order Configuration */}
      <Card className={`${getCardBorderColor()}`}>
        <CardContent className="p-4">
          <div className="space-y-4">
            {/* Stop Price */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">
                  Stop Price
                </Label>
                <div className="text-xs text-muted-foreground">
                  Bid: {formatPrice(bidPrice)} | Ask: {formatPrice(askPrice)}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPriceChange(-0.0001)}
                  className="h-8 w-8 p-0"
                >
                  <Minus size={14} />
                </Button>

                <Input
                  type="number"
                  value={priceInput}
                  onChange={(e) => onPriceInputChange(e.target.value)}
                  onClick={handlePriceClick}
                  placeholder="Not set"
                  className="text-center font-mono text-sm"
                  step="0.0001"
                />

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPriceChange(0.0001)}
                  className="h-8 w-8 p-0"
                >
                  <Plus size={14} />
                </Button>
              </div>
            </div>

            {/* Limit Price */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">
                  Limit Price
                </Label>
                <div className="text-xs text-muted-foreground">
                  Execution price
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onStopLimitPriceChange(-0.0001)}
                  className="h-8 w-8 p-0"
                >
                  <Minus size={14} />
                </Button>

                <Input
                  type="number"
                  value={stopLimitPriceInput}
                  onChange={(e) => onStopLimitPriceInputChange(e.target.value)}
                  onClick={handleStopLimitPriceClick}
                  placeholder="Not set"
                  className="text-center font-mono text-sm"
                  step="0.0001"
                />

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onStopLimitPriceChange(0.0001)}
                  className="h-8 w-8 p-0"
                >
                  <Plus size={14} />
                </Button>
              </div>
            </div>

            {/* Stop Loss */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">
                  Stop Loss
                </Label>
                <div className="text-xs text-muted-foreground">
                  Optional protection
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onStopLossChange(-0.0001)}
                  className="h-8 w-8 p-0"
                >
                  <Minus size={14} />
                </Button>

                <Input
                  type="number"
                  value={stopLossInput}
                  onChange={(e) => onStopLossInputChange(e.target.value)}
                  onClick={handleStopLossClick}
                  placeholder="Not set"
                  className="text-center font-mono text-sm"
                  step="0.0001"
                />

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onStopLossChange(0.0001)}
                  className="h-8 w-8 p-0"
                >
                  <Plus size={14} />
                </Button>
              </div>
            </div>

            {/* Take Profit */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">
                  Take Profit
                </Label>
                <div className="text-xs text-muted-foreground">
                  Optional target
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onTakeProfitChange(-0.0001)}
                  className="h-8 w-8 p-0"
                >
                  <Minus size={14} />
                </Button>

                <Input
                  type="number"
                  value={takeProfitInput}
                  onChange={(e) => onTakeProfitInputChange(e.target.value)}
                  onClick={handleTakeProfitClick}
                  placeholder="Not set"
                  className="text-center font-mono text-sm"
                  step="0.0001"
                />

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onTakeProfitChange(0.0001)}
                  className="h-8 w-8 p-0"
                >
                  <Plus size={14} />
                </Button>
              </div>
            </div>
            {/* Expiration - only for Stop Limit orders */}
            {supportsExpiration && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    Expiration
                  </Label>
                  <div className="text-xs text-muted-foreground">
                    Order validity
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Select
                    value={expirationType}
                    onValueChange={handleExpirationTypeChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue>{getExpirationDisplay()}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GTC">GTC (Good Till Cancelled)</SelectItem>
                      <SelectItem value="TODAY">Today</SelectItem>
                      <SelectItem value="SPECIFIED">Specified Date & Time</SelectItem>
                      <SelectItem value="SPECIFIED_DAY">Specified Day</SelectItem>
                    </SelectContent>
                  </Select>

                  {(expirationType === "SPECIFIED" || expirationType === "SPECIFIED_DAY") && (
                    <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <Calendar size={14} />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <CalendarComponent
                          mode="single"
                          selected={expirationDate}
                          onSelect={(date) => {
                            setExpirationDate(date);
                            if (expirationType === "SPECIFIED_DAY") {
                              setShowDatePicker(false);
                            }
                          }}
                          initialFocus
                        />
                        {expirationType === "SPECIFIED" && (
                          <div className="p-3 border-t">
                            <div className="flex items-center gap-2">
                              <Label className="text-sm">Time:</Label>
                              <Input
                                type="time"
                                value={expirationTime}
                                onChange={(e) => setExpirationTime(e.target.value)}
                                className="text-sm"
                              />
                              <Button
                                size="sm"
                                onClick={() => setShowDatePicker(false)}
                              >
                                OK
                              </Button>
                            </div>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Action bar — live prices + single PLACE ORDER (proposal 18) */}
      <div className="rounded-lg border border-border bg-background p-3">
        <div className="mb-2 flex items-center justify-center gap-6 font-mono text-xs tabular-nums">
          <span className="text-red-400">SELL {formatPrice(bidPrice)}</span>
          <span className="text-blue-400">BUY {formatPrice(askPrice)}</span>
        </div>
        <Button
          onClick={onPlaceOrder}
          className={`h-[52px] w-full rounded-lg text-white ${getButtonColor()}`}
          disabled={isTrading}
        >
          <span className="text-xs font-bold uppercase tracking-[0.12em]">
            {isTrading ? "PLACING…" : getButtonText()}
          </span>
        </Button>
      </div>
    </>
  );
}
